import { createServerFn } from '@tanstack/react-start';
import {
    and,
    count,
    countDistinct,
    eq,
    gte,
    sql,
} from 'drizzle-orm';
import { z } from 'zod';

import {
    ANALYTICS_MAX_RANGE_DAYS,
    fillDailySeries,
    inclusiveDayCount,
} from '@/lib/analytics-series';
import { hebrewReferrerLabel } from '@/lib/analytics-context';
import {
    hebrewContinentLabel,
    hebrewCountryLabel,
} from '@/lib/geo-labels';
import { getDb } from '@/db/client';
import { events } from '@/db/schema';
import { requireAdminAccess, requireStaffAccess } from '@/server/functions/auth';
import {
    loadAdAnalytics,
    loadSiteAnalyticsDetailed,
} from '@/server/lib/site-analytics';

function newId() {
    return crypto.randomUUID();
}

const ANALYTICS_CHART_DAYS = 30;
const ANALYTICS_WINDOW_DAYS = 7;

const trackEventSchema = z.object({
    visitorId: z.string().min(8).max(36),
    name: z.string().min(1).max(64),
    path: z.string().max(2048).nullable().optional(),
    articleId: z.string().max(36).nullable().optional(),
    meta: z.record(z.string(), z.unknown()).nullable().optional(),
    countryCode: z.string().max(2).nullable().optional(),
    country: z.string().max(100).nullable().optional(),
    continentCode: z.string().max(2).nullable().optional(),
    continent: z.string().max(64).nullable().optional(),
    deviceType: z
        .enum(['mobile', 'tablet', 'desktop'])
        .nullable()
        .optional(),
    referrerSource: z.string().max(128).nullable().optional(),
});

/** Public beacon - insert one analytics event. */
export const trackEventFn = createServerFn({ method: 'POST' })
    .validator(trackEventSchema)
    .handler(async ({ data }) => {
        const name = data.name.trim().slice(0, 64);
        if (!name) return { ok: false as const };

        // Skip obvious admin / auth beacons if a client still sends them.
        const path = data.path?.trim() || null;
        if (
            path &&
            (path.startsWith('/admin') ||
                path.startsWith('/sign-') ||
                path.startsWith('/recover'))
        ) {
            return { ok: false as const };
        }

        const db = getDb();
        const countryCode =
            data.countryCode?.trim().toUpperCase().slice(0, 2) || null;
        const continentCode =
            data.continentCode?.trim().toUpperCase().slice(0, 2) || null;
        const countryRaw = data.country?.trim() || null;
        const continentRaw = data.continent?.trim() || null;
        const deviceType = data.deviceType ?? null;
        const referrerSource =
            data.referrerSource?.trim().toLowerCase().slice(0, 128) || null;
        await db.insert(events).values({
            id: newId(),
            visitorId: data.visitorId.trim().slice(0, 36),
            name,
            path: path ? path.slice(0, 2048) : null,
            articleId: data.articleId?.trim().slice(0, 36) || null,
            meta: data.meta ?? null,
            countryCode,
            country:
                countryCode || countryRaw
                    ? hebrewCountryLabel(countryCode, countryRaw).slice(0, 100)
                    : null,
            continentCode,
            continent:
                continentCode || continentRaw
                    ? hebrewContinentLabel(continentCode, continentRaw).slice(
                          0,
                          64,
                      )
                    : null,
            deviceType,
            referrerSource,
        });
        return { ok: true as const };
    });

const articleAnalyticsSchema = z.object({
    articleId: z.string().min(1).max(36),
});

/** Per-article analytics for the admin article editor. */
export const adminArticleAnalyticsFn = createServerFn({ method: 'GET' })
    .validator(articleAnalyticsSchema)
    .handler(async ({ data }) => {
        await requireStaffAccess();
        const db = getDb();
        const articleId = data.articleId;
        const since7d = new Date(
            Date.now() - ANALYTICS_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        );
        const sinceChart = new Date(
            Date.now() - ANALYTICS_CHART_DAYS * 24 * 60 * 60 * 1000,
        );
        const dayExpr = sql<string>`to_char(${events.createdAt} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD')`;
        const articleFilter = and(
            eq(events.articleId, articleId),
            eq(events.name, 'article_view'),
        );

        const [
            [totalViews],
            [totalVisitors],
            [views7d],
            [visitors7d],
            viewsByDayRows,
            countryRows,
            referrerRows,
        ] = await Promise.all([
            db
                .select({ value: count() })
                .from(events)
                .where(articleFilter),
            db
                .select({ value: countDistinct(events.visitorId) })
                .from(events)
                .where(articleFilter),
            db
                .select({ value: count() })
                .from(events)
                .where(and(articleFilter, gte(events.createdAt, since7d))),
            db
                .select({ value: countDistinct(events.visitorId) })
                .from(events)
                .where(and(articleFilter, gte(events.createdAt, since7d))),
            db
                .select({
                    day: dayExpr,
                    value: count(),
                })
                .from(events)
                .where(and(articleFilter, gte(events.createdAt, sinceChart)))
                .groupBy(dayExpr)
                .orderBy(dayExpr),
            db
                .select({
                    code: events.countryCode,
                    label: sql<string | null>`max(${events.country})`,
                    views: count(),
                    visitors: countDistinct(events.visitorId),
                })
                .from(events)
                .where(and(articleFilter, gte(events.createdAt, since7d)))
                .groupBy(events.countryCode)
                .orderBy(sql`count(*) desc`)
                .limit(8),
            db
                .select({
                    key: events.referrerSource,
                    label: events.referrerSource,
                    views: count(),
                    visitors: countDistinct(events.visitorId),
                })
                .from(events)
                .where(and(articleFilter, gte(events.createdAt, sinceChart)))
                .groupBy(events.referrerSource)
                .orderBy(sql`count(*) desc`)
                .limit(8),
        ]);

        return {
            totalViews: totalViews?.value ?? 0,
            totalVisitors: totalVisitors?.value ?? 0,
            views7d: views7d?.value ?? 0,
            visitors7d: visitors7d?.value ?? 0,
            viewsPerDay: fillDailySeries(ANALYTICS_CHART_DAYS, viewsByDayRows),
            countries: countryRows.map((row) => ({
                code: row.code,
                label: hebrewCountryLabel(row.code, row.label),
                views: Number(row.views),
                visitors: Number(row.visitors),
            })),
            referrers: referrerRows.map((row) => ({
                key: row.key ?? 'direct',
                label: hebrewReferrerLabel(row.key),
                views: Number(row.views),
                visitors: Number(row.visitors),
            })),
            chartDays: ANALYTICS_CHART_DAYS,
            windowDays: ANALYTICS_WINDOW_DAYS,
        };
    });

const dayKeySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid day key');

const analyticsPageSchema = z
    .object({
        days: z
            .number()
            .int()
            .min(1)
            .max(ANALYTICS_MAX_RANGE_DAYS)
            .optional(),
        start: dayKeySchema.optional(),
        end: dayKeySchema.optional(),
    })
    .superRefine((value, ctx) => {
        const hasStart = Boolean(value.start);
        const hasEnd = Boolean(value.end);
        if (hasStart !== hasEnd) {
            ctx.addIssue({
                code: 'custom',
                message: 'Both start and end are required for a custom range',
                path: hasStart ? ['end'] : ['start'],
            });
            return;
        }
        if (!value.start || !value.end) return;
        const start = value.start <= value.end ? value.start : value.end;
        const end = value.start <= value.end ? value.end : value.start;
        const span = inclusiveDayCount(start, end);
        if (span < 1 || span > ANALYTICS_MAX_RANGE_DAYS) {
            ctx.addIssue({
                code: 'custom',
                message: `Date range must be 1-${ANALYTICS_MAX_RANGE_DAYS} days`,
                path: ['end'],
            });
        }
    });

/** Full analytics console page payload. */
export const adminAnalyticsPageFn = createServerFn({ method: 'GET' })
    .validator(analyticsPageSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        if (data.start && data.end) {
            const start = data.start <= data.end ? data.start : data.end;
            const end = data.start <= data.end ? data.end : data.start;
            return loadSiteAnalyticsDetailed({ start, end });
        }
        return loadSiteAnalyticsDetailed(data.days ?? 30);
    });

const adAnalyticsSchema = z.object({
    adId: z.string().min(1).max(36),
});

/** Per-ad impressions, visitors, and clicks for the ad editor. */
export const adminAdAnalyticsFn = createServerFn({ method: 'GET' })
    .validator(adAnalyticsSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        return loadAdAnalytics(data.adId);
    });
